import React, {useEffect, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {NavLink} from 'react-router-dom';
import {Breadcrumb} from '~/components/Breadcrumps';
import {Button} from '~/components/Button';
import {Input} from '~/components/Controls/Input';
import {SelectInput} from '~/components/Controls/Select';
import {TagsList} from '~/components/Controls/Tags';
import {Progress} from '~/components/Progress';
import {BreadcrumbsResourcePage} from '~/components/ResourcePage';
import {Table} from '~/components/Table';
import {useAbility} from '~/lib/ability';
import {Copy} from '~/lib/helpers';
import {Intent} from '~/lib/intent';
import {controllableUseResource} from '~/lib/loader';
import {clientsLinks} from '~/modules/Clients';
import {fetchCategories} from '~/modules/Clients/api';
import {MultiSelectIABCategories} from '~/modules/Clients/components/SelectIABCategories';
import {IABCategory} from '~/modules/Clients/models/IABCategory';
import {creativesLinks} from '~/modules/Creatives';
import {CreativesAbility} from '~/modules/Creatives/ability';
import {fetchTopCreatives} from '~/modules/Creatives/api';
import {AdType, adTypes, getAdTypeLabel, ICreative} from '~/modules/Creatives/models/Creative';
import {
    filterPeriods,
    ITopCreativesFilter, sortByOptions,
    TopCreativesFilterPeriod,
    TopCreativesSortByOptions
} from '~/modules/Creatives/models/filters';
import style from './TopCreativesPreview.module.scss';

export const TopCreativesPreview = () => {
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const ability = useAbility(CreativesAbility);

    const [submitted, setSubmitted] = useState(0);

    const [filters, setFilters] = useState<ITopCreativesFilter>({
        adCategory: null,
        adDomain: '',
        bundle: '',
        period: TopCreativesFilterPeriod.TODAY,
        sortBy: TopCreativesSortByOptions.IMPRESSION,
        categories: [],
    });

    const [iabCategoriesList, setIABCategories] = useState<IABCategory[]>([]);

    const [displayedCreative, setDisplayedCreative] = useState(undefined);

    const result = controllableUseResource(() => fetchTopCreatives(filters, ability.isAdmin() ? params.get('sellerId') : ability.sellerId()));
    useEffect(() => {
        fetchCategories().then(iabCats => {
            setIABCategories(iabCats);
        }).catch(() => {
            setIABCategories([]);
        });
    }, []);

    const submitHandler = (event) => {
        setSubmitted(1);
        event.preventDefault();

        if (ability.isSeller()) {
            filters.adDomain = '';
        }

        result.fetch();
    };

    const creativePreview = (creative: ICreative) => {
        const supportedAdTypes = [AdType.MREC, AdType.BANNER, AdType.INTERSTITIAL, AdType.NON_SKIPPABLE_INTERSTITIAL];
        if (supportedAdTypes.findIndex((t) => t === creative.adCategory) !== -1) {
            return displayedCreative === creative.hash
                ? <iframe className={style.creativeHtml} srcDoc={creative.adm}/>
                : <Button small
                    leftIcon={
                        <i className={'fa fa-eye'}/>}
                    onClick={() => {
                        setDisplayedCreative(creative.hash);
                    }}
                />;
        } else if (creative.adCategory === AdType.NATIVE) {
            return 'No preview';
        } else {
            return <a href='https://googleads.github.io/googleads-ima-html5/vsi/' target='_blank'>Preview</a>;
        }
    };

    const convertCategoryIdToLabel = (creativeCategories: string | undefined) => creativeCategories?.split(',').map(cat => {
        const category = iabCategoriesList.find(iabCat => iabCat.id === cat.trim());
        return category?.description || '';
    }) || [];

    return <Breadcrumb breadcrumb={{
        to: creativesLinks.topPreview(),
        title: 'Top creatives'
    }}><BreadcrumbsResourcePage title='Top creatives preview'>
            <div className="layout-content-box">
                <form className={style.creativesFilter + (submitted ? ' ' + style.isSubmitted : '')}
                    onSubmit={submitHandler}>

                    <SelectInput items={adTypes} value={filters.adCategory}
                        onChange={adCategory => setFilters({...filters, adCategory})}
                        placeholder='Ad Type' disabled={result.isPending} clearable
                        className={style.adTypeSelect}/>
                    <Input
                        value={filters.adDomain}
                        onChange={adDomain => setFilters({...filters, adDomain})}
                        placeholder='Domain'
                        disabled={result.isPending}
                        className={style.domainInput}
                        rightIcon={<i className='fa fa-search input-search-icon' />}
                    />
                    <Input
                        value={filters.bundle}
                        onChange={bundle => setFilters({...filters, bundle})}
                        placeholder='Bundle'
                        disabled={result.isPending}
                        className={style.bundleInput}
                        rightIcon={<i className='fa fa-search input-search-icon' />}
                    />
                    <MultiSelectIABCategories value={filters.categories}
                        onChange={categories => setFilters({...filters, categories})}
                        placeholder='IAB Categories' clearable searchable disabled={result.isPending}
                        className={style.iabCategoriesInput}/>
                    <SelectInput items={filterPeriods} value={filters.period}
                        onChange={period => setFilters({...filters, period})}
                        placeholder='Period' disabled={result.isPending}
                        className={style.periodSelect}/>
                    <SelectInput items={sortByOptions} value={filters.sortBy}
                        onChange={sortBy => setFilters({...filters, sortBy})}
                        placeholder='Sort by' disabled={result.isPending}
                        className={style.sortBySelect}/>
                    <Button onClick={() => setSubmitted(1)} type={'submit'} rightIcon={<i className='fa fa-search'/>}
                        intent={Intent.PRIMARY}
                        disabled={result.isPending}
                        className={style.searchButton}>Search</Button>
                </form>
            {result.isPending &&
            <div className={style.infoMessage}>Your first query may take 3-5 minutes</div>}
            {result.error &&
            <div className={style.errorMessage}>Error while query: {result.error}</div>}
            <Table header
                columns={[{
                    header: 'Updated',
                    field: 'updated',
                    cell: creative => new Date(creative.updated).toLocaleString()
                }, {
                    header: 'Ad Type',
                    field: 'adCategory',
                    cell: creative => getAdTypeLabel(creative.adCategory)
                }, {
                    header: 'Agency',
                    cell: creative => ability.isSeller() ? creative.agencyName : <NavLink
                        to={clientsLinks.agency(creative.agencyId)}>{creative.agencyName}</NavLink>
                },
                {
                    header: 'IAB Categories',
                    cellAttrs: {
                        style: {
                            maxWidth: '180px',
                            overflow: 'visible',
                            whiteSpace: 'normal'
                        }
                    },
                    cell: creative => <TagsList values={convertCategoryIdToLabel(creative.categories)} maxItems={3} size="small"/>
                }, {
                    header: 'CID',
                    field: 'cid'
                }, {
                    header: 'CRID',
                    field: 'crid'
                }, {
                    header: 'Domain',
                    field: 'adDomain'
                }, {
                    header: 'Bundle',
                    field: 'bundle'
                }, {
                    header: 'iURL',
                    cell: creative => <Copy
                        text={creative.iURL}/>
                }, {
                    header: 'Adm',
                    cell: creative => <Copy text={creative.adm || ''}/>
                }, {
                    header: 'Adm preview',
                    cell: creative => creativePreview(creative)
                }]}
                records={result.resource || []}
                placeholder={result.isPending && <Progress/>}/>
            </div>
        </BreadcrumbsResourcePage></Breadcrumb>;
};
